/**
 * The search box's suggestions at the caret (#2290): the grammar's own
 * values at once (`staticSuggestions`), tags, people, functionals, models and groups
 * from the server once typing pauses.
 *
 * A lookup's answer is shown only for the context it was asked for, and only
 * if it is the latest asked: an older, slower answer never replaces a newer
 * one.
 */
import { useEffect, useRef, useState } from 'react';
import { searchModelTags } from '@/api/models';
import { searchEntities } from './entityLookups';
import { ME_SUGGESTION, remoteKind, staticSuggestions, valueSuggestion, type SuggestContext, type Suggestion } from './searchSuggest';
import type { EntityKind } from './searchSyntax';

const MAX = 8;

async function lookUp(kind: 'tag' | EntityKind, prefix: string, exclude: string[]): Promise<Suggestion[]> {
  if (kind === 'tag') {
    const tags = await searchModelTags(prefix.trim());
    return tags
      .filter((t) => !exclude.includes(t.tag))
      .slice(0, MAX)
      .map((t) => valueSuggestion('tag', t.tag, t.tag, t.count > 0 ? String(t.count) : undefined));
  }
  // an either-or group's other names are not offered again
  const hits = await searchEntities(kind, prefix, MAX);
  return hits.filter((h) => !exclude.includes(h.label)).map((h) => valueSuggestion(kind, h.id, h.label));
}

export interface SearchSuggestions {
  items: Suggestion[];
  /** A lookup for this context has not answered yet. */
  loading: boolean;
}

export function useSearchSuggestions(ctx: SuggestContext | null, debounceMs = 200): SearchSuggestions {
  const remote = ctx ? remoteKind(ctx) : null;
  const key = ctx && remote ? `${remote}\n${ctx.prefix}\n${ctx.exclude.join('|')}` : null;
  const [answer, setAnswer] = useState<{ key: string; items: Suggestion[] } | null>(null);
  const seq = useRef(0);

  useEffect(() => {
    if (!key || !remote || !ctx) return;
    const asked = ++seq.current;
    const timer = setTimeout(() => {
      lookUp(remote, ctx.prefix, ctx.exclude).then(
        (items) => { if (seq.current === asked) setAnswer({ key, items }); },
        () => { if (seq.current === asked) setAnswer({ key, items: [] }); },
      );
    }, debounceMs);
    return () => clearTimeout(timer);
    // `key` holds everything the lookup reads from `ctx`.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional-deps: key holds everything the lookup reads from ctx and remote
  }, [key, debounceMs]);

  // Unmounting or moving on invalidates whatever is still in flight.
  useEffect(() => () => { seq.current += 1; }, []);

  if (!ctx) return { items: [], loading: false };
  const fixed = staticSuggestions(ctx);
  if (fixed) return { items: fixed, loading: false };
  const fetched = answer?.key === key ? answer.items : null;
  const me = ctx.kind === 'author' && 'me'.startsWith(ctx.prefix.toLowerCase()) ? [ME_SUGGESTION] : [];
  return { items: [...me, ...(fetched ?? [])], loading: fetched === null };
}
